import { ARTIFACTS_BY_ID } from '../artifacts/ArtifactDefs';
import type { Profile, ProfileData } from './Profile';
import { dupShardValue, gemShardYield, roundQuality } from './economy';
import { evaluateMilestones, type Milestone } from './milestones';
import { maxUnlockedTier, tierTitheMult } from '../run/tiers';

/**
 * Settlement (pure, headless). Banking happens at every CHECKPOINT — each
 * floor descent and the final win:
 * - Found artifacts bank at their rolled quality. Unowned → new entry; better
 *   than the banked roll → replaces it (old copy converts to shards);
 *   equal/worse → converts to shards.
 * - Death loses only the CURRENT floor's unbanked finds (earlier floors were
 *   settled at descent) — roguelite progress survives dying.
 * Brought artifacts never appear in `foundArtifacts` — they are SAFE.
 */

export interface FoundArtifact {
  artifactId: string;
  quality: number;
}

/** The banking half of a settlement — shared by descent checkpoints and wins. */
export interface BankSummary {
  bankedNew: FoundArtifact[];
  bankedImproved: { artifactId: string; from: number; to: number }[];
  converted: { artifactId: string; shards: number }[];
  shardsGained: number;
}

export interface RunEndSummary extends BankSummary {
  lost: FoundArtifact[];
  /**
   * Run-end gem tithe (included in shardsGained). Lives on RunEndSummary,
   * NOT BankSummary — RunScene.endRun's display merge spreads the accumulated
   * BankSummary over the settlement, clobbering any BankSummary field.
   */
  shardsFromGems: number;
  shardBalance: number;
  unlocked: Milestone[];
  /** Ladder: per-tier best time was set or beaten (win + recordEligible only). */
  newRecord: boolean;
  /** Ladder: newly reachable tier (maxUnlockedTier rose), else null. */
  unlockedTier: number | null;
}

export function emptyBankSummary(): BankSummary {
  return { bankedNew: [], bankedImproved: [], converted: [], shardsGained: 0 };
}

/** Display-only accumulation: appends `from`'s entries onto `into`. */
export function mergeBankSummary(into: BankSummary, from: BankSummary): void {
  into.bankedNew.push(...from.bankedNew);
  into.bankedImproved.push(...from.bankedImproved);
  into.converted.push(...from.converted);
  into.shardsGained += from.shardsGained;
}

function bankFound(data: ProfileData, found: FoundArtifact[], summary: BankSummary): void {
  for (const f of found) {
    if (!ARTIFACTS_BY_ID.has(f.artifactId)) continue;
    const q = roundQuality(f.quality);
    const owned = data.collection[f.artifactId];
    if (owned === undefined) {
      data.collection[f.artifactId] = q;
      summary.bankedNew.push({ artifactId: f.artifactId, quality: q });
    } else if (q > owned) {
      // Better roll replaces; the old copy liquidates at ITS quality.
      const shards = dupShardValue(owned);
      data.collection[f.artifactId] = q;
      data.shards += shards;
      summary.bankedImproved.push({ artifactId: f.artifactId, from: owned, to: q });
      summary.converted.push({ artifactId: f.artifactId, shards });
      summary.shardsGained += shards;
    } else {
      const shards = dupShardValue(q);
      data.shards += shards;
      summary.converted.push({ artifactId: f.artifactId, shards });
      summary.shardsGained += shards;
    }
  }
}

/**
 * Descent checkpoint: banks the departing floor's finds exactly like a win
 * settlement. NO wins/deaths/bestFloor/milestones — those remain run-end
 * concerns. No-op (no save) when nothing was found.
 */
export function applyDescentBank(
  profile: Profile,
  args: { foundArtifacts: FoundArtifact[] },
): BankSummary {
  const summary = emptyBankSummary();
  if (args.foundArtifacts.length === 0) return summary;
  bankFound(profile.data, args.foundArtifacts, summary);
  profile.save();
  return summary;
}

export function applyRunEnd(
  profile: Profile,
  args: {
    win: boolean; floor: number; foundArtifacts: FoundArtifact[]; gemsEarned: number;
    /** Ladder tier the run was played at (0 = base game). */
    tier: number;
    /** Sim-tick run time, seconds — the ladder's leaderboard clock. */
    timeSeconds: number;
    /** False for cheat runs (?god/?floor/?grant/?tier): no records written. */
    recordEligible: boolean;
    /** Run kill total — accumulates into stats.totalKills (default 0). */
    kills?: number;
    /** Character the run was played as — stamped onto ladder records. */
    character?: string;
  },
): RunEndSummary {
  const data = profile.data;
  const summary: RunEndSummary = {
    ...emptyBankSummary(),
    lost: [],
    shardsGained: 0,
    shardsFromGems: 0,
    shardBalance: 0,
    unlocked: [],
    newRecord: false,
    unlockedTier: null,
  };

  // Snapshot BEFORE the wins++ below: maxUnlockedTier's grandfather branch
  // reads stats.wins, and bumping first would swallow the very first unlock.
  const unlockedBefore = maxUnlockedTier(data);

  if (args.win) {
    data.stats.wins++;
    bankFound(data, args.foundArtifacts, summary);
  } else {
    data.stats.deaths++;
    summary.lost = args.foundArtifacts.map((f) => ({
      artifactId: f.artifactId,
      quality: roundQuality(f.quality),
    }));
  }

  // Ladder records — wins only, cheat runs excluded. Unlock detection is a
  // before/after comparison of the DERIVED maxUnlockedTier, so a grandfathered
  // profile (wins>0, empty bests) re-clearing tier 0 records a best without a
  // spurious "unlocked" line.
  if (args.win && args.recordEligible) {
    const key = String(args.tier);
    const prev = data.ladder.bests[key];
    if (prev === undefined || args.timeSeconds < prev.seconds) {
      data.ladder.bests[key] = {
        seconds: Math.round(args.timeSeconds * 100) / 100,
        character: args.character ?? '',
        kills: Math.max(0, Math.floor(args.kills ?? 0)),
      };
      summary.newRecord = true;
    }
    const after = maxUnlockedTier(data);
    if (after > unlockedBefore) {
      summary.unlockedTier = after;
      // Auto-advance: the sticky pre-run pick climbs to the new frontier.
      data.lastTier = after;
    }
  }

  // Gem tithe — every run, win or death (economy.ts rates, tier-boosted).
  // Cheat runs (?tier etc.) tithe at the base rate: records are guarded above,
  // and this keeps ?tier=40 from minting shards at ×5.
  const titheMult = args.recordEligible ? tierTitheMult(args.tier) : 1;
  const fromGems = gemShardYield(args.gemsEarned, args.win, titheMult);
  data.shards += fromGems;
  summary.shardsFromGems = fromGems;
  summary.shardsGained += fromGems;

  data.stats.bestFloor = Math.max(data.stats.bestFloor, args.floor);
  // Lifetime counters (character gates): every run counts, cheat or not —
  // they gate sideways choices, not records.
  data.stats.totalKills += Math.max(0, Math.floor(args.kills ?? 0));
  data.stats.totalGems += Math.max(0, Math.round(args.gemsEarned));
  summary.unlocked = evaluateMilestones(data, { win: args.win, floor: args.floor });
  summary.shardBalance = data.shards;
  profile.save();
  return summary;
}
